/**
 * Place-name search over Photon (photon.komoot.io) — ported from the main product's
 * `useLocationSearch`, which has run these two numbers in production.
 *
 * Three things are different here, and each has a reason:
 *
 *   1. `failed` is a state of its own. The source shows «ничего не найдено» for a 502 and for
 *      an honest empty answer alike. For us that conflation is worse than it is for them:
 *      coordinates work with the network down (lib/coords.ts), so the one thing the operator
 *      needs at that moment is WHICH of the two halves is out.
 *   2. `lang=default` rather than `en`. Photon accepts default/de/en/fr and rejects `ru`
 *      outright, so the locale cannot be forwarded naively; `default` returns the local `name`
 *      and never swaps «Москва» for whatever `name:en` holds.
 *   3. The viewport biases the answer. Photon is fuzzy — `Бзов` comes back as Бзовик in
 *      Serbia — and `lat`/`lon` are a free priority nudge toward where the map is already
 *      looking. Same one request, same 350 ms, no Earth Engine anywhere near it.
 *
 * The bias lives in a ref on purpose: panning the map must not re-issue a search that is
 * already on screen.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import type { PhotonFeature } from "../lib/places";

const PHOTON_URL = "https://photon.komoot.io/api/";
const DEBOUNCE_MS = 350;
const RESULT_LIMIT = 5;

/** Below this every second keystroke is a request and none of them mean anything. */
export const MIN_QUERY_LENGTH = 2;

interface PhotonResponse {
  type: "FeatureCollection";
  features?: PhotonFeature[];
}

export interface PlaceSearch {
  results: PhotonFeature[];
  loading: boolean;
  /** The search itself is out — not «nothing was found». Different sentence, different fix. */
  failed: boolean;
  search: (query: string) => void;
  clear: () => void;
}

/** @param near `[lon, lat]` of the current view, in the order `Viewport.center` has it. */
export const usePlaceSearch = (near: [number, number] | null): PlaceSearch => {
  const [results, setResults] = useState<PhotonFeature[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abort = useRef<AbortController | null>(null);
  const nearRef = useRef<[number, number] | null>(near);

  useEffect(() => {
    nearRef.current = near;
  }, [near]);

  const stop = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    if (abort.current) {
      abort.current.abort();
      abort.current = null;
    }
  }, []);

  const search = useCallback(
    (query: string) => {
      stop();
      if (query.length < MIN_QUERY_LENGTH) {
        setResults([]);
        setLoading(false);
        setFailed(false);
        return;
      }
      // Lit before the timer, not after it: the spinner belongs to the keystroke, otherwise
      // the box looks inert for the whole debounce.
      setLoading(true);
      setFailed(false);

      timer.current = setTimeout(() => {
        const controller = new AbortController();
        abort.current = controller;
        const params = new URLSearchParams({
          q: query,
          limit: String(RESULT_LIMIT),
          lang: "default",
        });
        const bias = nearRef.current;
        if (bias) {
          params.set("lon", bias[0].toFixed(4));
          params.set("lat", bias[1].toFixed(4));
        }

        void (async () => {
          try {
            const response = await fetch(`${PHOTON_URL}?${params.toString()}`, {
              signal: controller.signal,
            });
            if (!response.ok) throw new Error(`Photon ${response.status}`);
            const data = (await response.json()) as PhotonResponse;
            setResults(data.features ?? []);
            setFailed(false);
          } catch (error: unknown) {
            if (error instanceof Error && error.name === "AbortError") return;
            console.warn("[usePlaceSearch]", error);
            setResults([]);
            setFailed(true);
          } finally {
            // Only if this request is still the current one — a newer search has already
            // lit the spinner and must not have it put out from under it.
            if (!controller.signal.aborted) setLoading(false);
          }
        })();
      }, DEBOUNCE_MS);
    },
    [stop],
  );

  const clear = useCallback(() => {
    stop();
    setResults([]);
    setLoading(false);
    setFailed(false);
  }, [stop]);

  useEffect(() => stop, [stop]);

  return { results, loading, failed, search, clear };
};
